import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { merge, Observable, of, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { map } from 'rxjs/operators/map';
import {
  SpecialOptionValueConfig,
  SpecialValueKey
} from 'src/app/form-template/form-template-model';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { countryCodes } from 'src/assets/country-code';

@Component({
  selector: 'csi-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.css']
})
export class PhoneNumberInputComponent implements OnInit, OnDestroy {
  @Input() phoneControl: FormControl;
  @Input() countryCodeControl: FormControl;
  @Input() set actionButtonLabel(actionButtonLabel: string) {
    this.matSpinnerOptions = {
      active: false,
      text: actionButtonLabel,
      spinnerSize: 19,
      mode: 'indeterminate'
    };
  }
  @Input() showActionButton: boolean;
  @Input() set isLoading(isLoading: boolean) {
    this.matSpinnerOptions.active = isLoading;
  }
  @Input() phoneNumberLabel: String = 'Phone Number';

  @Output() actionButtonPressed = new EventEmitter();
  @Input() specialOptions: { [key in SpecialValueKey]?: SpecialOptionValueConfig } = {};

  public countryCodes = countryCodes;
  public matSpinnerOptions: MatProgressButtonOptions = null;
  public typeahead = new Subject<string>();
  public countryCodes$ = new Observable<any[]>();
  private countryCode = '';
  specialOptionsFormGroup: FormGroup = new FormGroup({});
  constructor(private clinicSetupService: ClinicSetupService) {}

  ngOnInit() {
    this.countryCode =
      this.clinicSetupService.clinic && this.clinicSetupService.clinic.country
        ? this.clinicSetupService.clinic.country
        : window.location.href.split(/[=&]+/)[1];
    this.initCountryCodeOptions();

    if (this.countryCode && !this.countryCodeControl.value) {
      this.countryCodeControl.setValue(
        `+${getCountryCallingCode(this.countryCode as CountryCode)}`
      );
    }

    if (!this.countryCode) {
      this.countryCodeControl.setValue(`+1`);
    }
    if (this.specialOptions) {
      Object.keys(this.specialOptions).forEach(key => {
        this.specialOptionsFormGroup.addControl(key, new FormControl());
      });

      if (this.specialOptionsFormGroup.controls[SpecialValueKey.NONE]) {
        this.specialOptionsFormGroup.controls[SpecialValueKey.NONE].valueChanges
          .pipe(untilDestroyed(this))
          .subscribe(selected => {
            if (selected) {
              this.phoneControl.patchValue(null);
              this.phoneControl.disable();
              this.countryCodeControl.disable();
            } else {
              this.phoneControl.enable();
              this.countryCodeControl.enable();
            }
          });
      }
    }
  }

  ngOnDestroy() {}

  initCountryCodeOptions() {
    this.countryCodes$ = merge(
      this.typeahead.pipe(
        debounceTime(100),
        map(item => this.searchCountryCodes(item))
      ),
      of(this.countryCodes)
    );
  }

  searchCountryCodes(userInput: string | null) {
    if (!userInput || !userInput.trim()) {
      return this.countryCodes;
    }
    userInput = userInput.toLowerCase().trim();
    const matchers = this.countryCodes.filter(
      countryCode =>
        countryCode.code.toLowerCase().includes(userInput) ||
        countryCode.countries.some(
          country =>
            country.iso.toLowerCase().includes(userInput) ||
            country.name.toLowerCase().includes(userInput) ||
            country.synonyms.some(synonym => synonym.toLowerCase().includes(userInput))
        )
    );
    return matchers;
  }
}
